export type QuestionCategory =
  | "general"
  | "technical"
  | "features"
  | "pricing"
  | "support"
  | "safety"
  | "privacy"
  | "accessibility"
  | "business"
  | "clinical";

export interface Question {
  question: string;
  answer: string;
  category: QuestionCategory;
}

const questions: Question[] = [
  {
    question: "What is AnamVR?",
    answer:
      "AnamVR is a mental wellness platform that uses virtual reality and cognitive behavioural therapy techniques to support your mental health.",
    category: "general",
  },
  {
    question: "How do I download or install AnamVR?",
    answer:
      "You can download AnamVR from the App Store or Google Play. Visit our Download page for direct links.",
    category: "technical",
  },
  {
    question: "How does AnamVR work?",
    answer:
      "AnamVR offers immersive VR experiences and guided exercises designed by mental health professionals to help you relax, focus, and improve your wellbeing.",
    category: "general",
  },
  {
    question: "Is AnamVR free? What's the pricing?",
    answer:
      "AnamVR offers both free and premium plans. You can try the basic features for free, or upgrade for more content and features.",
    category: "pricing",
  },
  {
    question: "What devices are supported?",
    answer:
      "AnamVR works with most VR headsets and is compatible with iPhone and Android smartphones (4.7–7.2 inch displays).",
    category: "technical",
  },
  {
    question: "Can I use AnamVR without a headset?",
    answer:
      "Some features can be accessed without a VR headset, but for the best experience, we recommend using a compatible headset.",
    category: "technical",
  },
  {
    question: "What's included in Premium?",
    answer:
      "Premium includes access to exclusive VR experiences, advanced tracking, and personalised wellness plans.",
    category: "pricing",
  },
  {
    question: "How do I contact support?",
    answer:
      "You can contact our support team through the Contact page or by emailing support@anamvr.com.",
    category: "support",
  },
  {
    question: "Is my data private and secure?",
    answer:
      "Yes, your privacy is important to us. We never share your personal data without your consent.",
    category: "privacy",
  },
  {
    question: "Who created AnamVR?",
    answer:
      "AnamVR was created by a team of mental health professionals and technologists passionate about improving wellbeing through technology.",
    category: "general",
  },
  {
    question: "Can AnamVR help with anxiety or stress?",
    answer:
      "Yes, AnamVR offers VR exercises and guided meditations specifically designed to help manage anxiety and stress.",
    category: "features",
  },
  {
    question: "How do I reset my password?",
    answer:
      "To reset your password, go to the login screen and click 'Forgot Password', then follow the instructions.",
    category: "support",
  },
  {
    question: "Where can I buy a headset?",
    answer: "You can purchase the AnamVR headset directly from our Shop page.",
    category: "technical",
  },
  {
    question: "How do I update the app?",
    answer:
      "Updates are available through your device's app store. Make sure to keep AnamVR updated for the latest features.",
    category: "technical",
  },
  {
    question: "How do I cancel my subscription?",
    answer:
      "You can manage or cancel your subscription in your account settings or through your app store subscriptions.",
    category: "pricing",
  },
  {
    question: "How do I start using AnamVR?",
    answer:
      "Download the app, create an account, and follow the simple setup instructions for your headset.",
    category: "general",
  },
  {
    question: "Is AnamVR available internationally?",
    answer:
      "Yes, AnamVR is available globally. As long as you have a compatible smartphone and VR headset, you can access the app from anywhere.",
    category: "general",
  },
  {
    question: "What languages are supported?",
    answer:
      "Currently, AnamVR is available in English. We're working on support for additional languages based on demand.",
    category: "accessibility",
  },
  {
    question: "What kind of VR experiences are included?",
    answer:
      "AnamVR includes mindfulness journeys, breathing exercises, guided meditations, CBT-based activities, and immersive nature environments.",
    category: "features",
  },
  {
    question: "How often is new content added?",
    answer:
      "We regularly add new experiences and wellness modules every month for premium users, based on feedback and current mental health trends.",
    category: "features",
  },
  {
    question: "Can I repeat sessions?",
    answer:
      "Yes, you can revisit any session as often as you like. Many users return to their favourites for ongoing support.",
    category: "features",
  },
  {
    question: "Is content personalised?",
    answer:
      "Yes, premium users can access personalised wellness journeys based on mood check-ins and preferences.",
    category: "features",
  },
  {
    question: "Can I track my progress?",
    answer:
      "Yes, the app includes simple dashboards to track usage, mood changes, and completed sessions.",
    category: "features",
  },
  {
    question: "Is AnamVR clinically validated?",
    answer:
      "AnamVR is based on proven cognitive behavioural therapy principles and was developed with input from mental health professionals.",
    category: "clinical",
  },
  {
    question: "Is this a replacement for therapy?",
    answer:
      "AnamVR is a supportive self-help tool and not a replacement for professional therapy or medical advice.",
    category: "clinical",
  },
  {
    question: "Can my therapist monitor my use?",
    answer:
      "You can share your progress reports with your therapist if you wish, but AnamVR does not currently integrate directly with clinical systems.",
    category: "clinical",
  },
  {
    question: "Is there a version for businesses?",
    answer:
      "Yes, we offer tailored packages for organisations looking to support their teams' mental wellness at scale.",
    category: "business",
  },
  {
    question: "Can I get a demo for my company?",
    answer:
      "Absolutely. Contact our sales team to book a live demo or get a custom quote.",
    category: "business",
  },
  {
    question: "Can I get a refund?",
    answer:
      "Refunds are handled via your app store provider and follow their policies. If you need help, reach out to support.",
    category: "pricing",
  },
  {
    question: "Can I switch billing plans?",
    answer:
      "Yes, you can switch plans anytime via your account settings in the app or your app store subscription settings.",
    category: "pricing",
  },
  {
    question: "Can I use AnamVR offline?",
    answer:
      "Some experiences can be downloaded in advance for offline use, especially for premium users.",
    category: "technical",
  },
  {
    question: "Can I use headphones or earbuds?",
    answer:
      "Yes, we recommend using quality headphones or earbuds to enhance the immersive audio experience.",
    category: "technical",
  },
  {
    question: "The app isn't working—what should I do?",
    answer:
      "Please try restarting or reinstalling the app. If the issue continues, contact support with details about your device.",
    category: "support",
  },
  {
    question: "Do I need your headset specifically?",
    answer:
      "No. AnamVR supports most smartphone VR headsets that are compatible with your device.",
    category: "technical",
  },
  {
    question: "Are you adding AI or new features?",
    answer:
      "Yes, we're exploring AI-based support and adaptive experiences as part of our future roadmap.",
    category: "features",
  },
  {
    question: "How can I send feedback or request a feature?",
    answer:
      "We'd love to hear from you! Use the in-app feedback form or email feedback@anamvr.com.",
    category: "support",
  },
  {
    question: "Is this suitable for sleep or focus?",
    answer:
      "Yes, our library includes VR sessions for improving sleep, focus, and emotional regulation.",
    category: "features",
  },
  {
    question: "Can I use it in the workplace?",
    answer:
      "Yes, AnamVR is ideal for workplace wellness programmes and scalable employee support.",
    category: "business",
  },
  {
    question: "Is there a trial for businesses?",
    answer:
      "Yes, business trials are available. Contact our team for access to pilot options.",
    category: "business",
  },
  {
    question: "Can I share my data with someone like a therapist?",
    answer:
      "Yes, you can export your progress manually and share it with any professional you choose.",
    category: "privacy",
  },
  {
    question: "Can I use AnamVR if I wear glasses?",
    answer:
      "Yes, most VR headsets compatible with AnamVR are designed to be worn comfortably over glasses.",
    category: "accessibility",
  },
  {
    question:
      "Does AnamVR work for people with motion sickness or sensitivity to VR?",
    answer:
      "AnamVR scenes are carefully designed to minimise motion and discomfort. You can start with short sessions and use non VR related features.",
    category: "accessibility",
  },
  {
    question: "Is there a non-VR mode for accessibility purposes?",
    answer:
      "Yes, AnamVR can be used in 2D mode on your phone screen, providing access to most features without a headset.",
    category: "accessibility",
  },
  {
    question: "How long should I use AnamVR each day?",
    answer:
      "We recommend 5–15 minutes per session. Users can explore at their own pace depending on their needs and preferences.",
    category: "safety",
  },
  {
    question: "Are there age restrictions for using AnamVR?",
    answer:
      "AnamVR is recommended for users aged 18 and above. Parental guidance is advised for younger users.",
    category: "safety",
  },
  {
    question:
      "Does AnamVR work with screen readers or other assistive technologies?",
    answer:
      "Basic support is available, and we are working to enhance accessibility for users who rely on assistive technologies.",
    category: "accessibility",
  },
  {
    question: "Can I use the same account across multiple devices?",
    answer:
      "Yes, your AnamVR account can be accessed from different devices, and your progress is synced securely via the cloud.",
    category: "technical",
  },
  {
    question: "How do I manage notifications or reminders?",
    answer:
      "You can enable, disable, or customise notifications from within the app settings.",
    category: "technical",
  },
  {
    question: "How do I delete my account and data?",
    answer:
      "Go to Settings > Account > Delete Account. This will permanently remove your data from our servers.",
    category: "privacy",
  },
  {
    question: "Can I use AnamVR with multiple profiles (e.g., family members)?",
    answer:
      "Currently, each account is intended for individual use. We're exploring multi-user features for future updates.",
    category: "technical",
  },
  {
    question: "What evidence supports AnamVR's effectiveness?",
    answer:
      "AnamVR is grounded in CBT principles and is being validated through partnerships and ongoing pilot studies.",
    category: "clinical",
  },
  {
    question: "Who are your clinical advisors or partners?",
    answer:
      "AnamVR collaborates with licensed therapists, psychologists, and academic institutions to ensure quality and safety.",
    category: "clinical",
  },
  {
    question: "How is content developed or reviewed?",
    answer:
      "All content is developed in collaboration with clinicians, reviewed for efficacy, and tested with user feedback.",
    category: "clinical",
  },
  {
    question: "Has AnamVR been used in clinical studies or trials?",
    answer:
      "Yes, AnamVR is currently being evaluated in pilot studies with research institutions.",
    category: "clinical",
  },
  {
    question: "Can AnamVR integrate with our HR or wellbeing platform?",
    answer:
      "Yes, we offer enterprise integrations and API support for HR systems and wellness dashboards.",
    category: "business",
  },
  {
    question: "Is employee usage anonymous?",
    answer:
      "Yes, AnamVR anonymises employee data in B2B reports to protect individual privacy while supporting wellbeing insights.",
    category: "business",
  },
  {
    question: "Can I get analytics or usage reports for my team?",
    answer:
      "Yes, team usage dashboards and progress tracking are available for enterprise customers.",
    category: "business",
  },
  {
    question: "Is there onboarding support for companies?",
    answer:
      "Yes, we provide onboarding sessions and support materials for enterprise clients.",
    category: "business",
  },
  {
    question: "Where is my data stored?",
    answer:
      "All data is securely stored in GDPR-compliant servers located in the EU.",
    category: "privacy",
  },
  {
    question: "How long is my data retained?",
    answer:
      "Data is retained for as long as you have an account. You can request deletion at any time.",
    category: "privacy",
  },
  {
    question: "What happens to my data if I cancel my subscription?",
    answer:
      "Your data is preserved securely and remains accessible if you reactivate. You can request deletion if preferred.",
    category: "privacy",
  },
  {
    question: "What's the minimum phone spec required for VR use?",
    answer:
      "A modern smartphone (iOS 13+/Android 9+) with a gyroscope and at least 3GB RAM is recommended.",
    category: "technical",
  },
  {
    question: "Do I need controllers to use the app?",
    answer:
      "No controllers are needed. The app is designed for gaze-based input and screen taps.",
    category: "technical",
  },
  {
    question: "Can I use AnamVR with Meta Quest or other high-end headsets?",
    answer:
      "AnamVR is currently optimised for smartphone VR. Support for standalone VR is planned.",
    category: "technical",
  },
  {
    question: "Is the content culturally sensitive and inclusive?",
    answer:
      "Yes, content is developed with input from diverse users and mental health professionals across cultures.",
    category: "accessibility",
  },
  {
    question:
      "Are there different programmes for different needs (e.g. burnout, trauma)?",
    answer:
      "Yes, content is organised into themes such as anxiety, burnout, sleep, and workplace stress.",
    category: "features",
  },
  {
    question: "Does AnamVR include guided breathing or meditation?",
    answer:
      "Yes, the app features guided breathing, mindfulness, and visual relaxation scenes.",
    category: "features",
  },
  {
    question: "Is there a community or peer support feature?",
    answer:
      "Not yet, but we're exploring ways to introduce moderated communities and group sessions.",
    category: "features",
  },
  {
    question: "How do I become a beta tester?",
    answer:
      "AnamVR is already live and on the appstore, the best thing is, its free! Check it out.",
    category: "general",
  },
  {
    question:
      "Can I contribute content or collaborate as a therapist or partner?",
    answer:
      "Yes, we welcome collaboration with clinicians and wellbeing experts. Please email us at hello@anamvr.com and we will get back to you.",
    category: "clinical",
  },
];

export default questions;
